import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, concatMap, map, switchMap } from 'rxjs/operators';
import { AuthOperationsService } from '../../../auth/services/auth-operations.service';
import type { ClassLinkInfo, CleverInfo } from '../../../models/ClassLinkCleverSyncInfo';
import type { GSuiteOrgs } from '../../../models/GSuiteOrgs';
import type { SchoolSyncInfo } from '../../../models/SchoolSyncInfo';
import { AdminService } from '../../../services/admin.service';
import * as schoolsActions from '../actions';

@Injectable()
export class SchoolsEffects {
	private actions$ = inject(Actions);
	private adminService = inject(AdminService);
	private authOperations = inject(AuthOperationsService);

	showErrorToast$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(schoolsActions.getSchoolsFailure),
			switchMap(() => {
				void this.authOperations.logoutLocally();
				return [schoolsActions.errorToastSuccess(), schoolsActions.clearSchools()];
			})
		);
	});

	getSchoolSyncInfo$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(schoolsActions.getSchoolSyncInfo),
			concatMap(() => {
				return this.adminService.getSpSyncing().pipe(
					map((syncInfo: SchoolSyncInfo) => {
						return schoolsActions.getSchoolSyncInfoSuccess({ syncInfo });
					}),
					catchError((error: Error) => of(schoolsActions.getSchoolSyncInfoFailure({ errorMessage: error.message })))
				);
			})
		);
	});

	updateSchoolSyncInfo$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(schoolsActions.updateSchoolSyncInfo),
			concatMap((action) => {
				return this.adminService.updateSpSyncing(action.data).pipe(
					map((syncInfo) => {
						if (action.data.selector_students) {
							return schoolsActions.updateGSuiteInfoSelectors({ selectors: action.data });
						}
						return schoolsActions.updateSchoolSyncInfoSuccess({ syncInfo });
					}),
					catchError((error) => of(schoolsActions.updateSchoolSyncInfoFailure({ errorMessage: error.message })))
				);
			})
		);
	});

	getGSuiteInfo$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(schoolsActions.getGSuiteSyncInfo),
			concatMap((action) => {
				return this.adminService.getGSuiteOrgs().pipe(
					map((gSuiteInfo: GSuiteOrgs) => {
						return schoolsActions.getGSuiteSyncInfoSuccess({ gSuiteInfo });
					}),
					catchError((error) => of(schoolsActions.getGSuiteSyncInfoFailure({ errorMessage: error.message })))
				);
			})
		);
	});

	updateGSuiteInfoSelectors$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(schoolsActions.updateGSuiteInfoSelectors),
			map((action) => {
				const selectors = {
					admin: {
						selector: action.selectors.selector_admins,
					},
					student: {
						selector: action.selectors.selector_students,
					},
					teacher: {
						selector: action.selectors.selector_teachers,
					},
					assistant: {
						selector: action.selectors.selector_assistants,
					},
				} as any;
				return schoolsActions.updateGSuiteInfoSelectorsSuccess({ selectors });
			})
		);
	});

	getSchoolCleverInfo$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(schoolsActions.getCleverInfo),
			switchMap((action: any) => {
				return this.adminService.getCleverInfo().pipe(
					map((cleverInfo: CleverInfo) => {
						return schoolsActions.getCleverInfoSuccess({ cleverInfo });
					}),
					catchError((error) => of(schoolsActions.getCleverInfoFailure({ errorMessage: error.message })))
				);
			})
		);
	});

	getSchoolClassLinkInfo$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(schoolsActions.getClassLinkInfo),
			switchMap((action: any) => {
				return this.adminService.getClassLinkSyncInfo().pipe(
					map((classLinkInfo: ClassLinkInfo) => {
						return schoolsActions.getClassLinkSuccess({ classLinkInfo });
					}),
					catchError((error) => of(schoolsActions.getClassLinkFailure({ errorMessage: error.message })))
				);
			})
		);
	});
}
